import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { providersAPI, categoriesAPI } from '../services/api';
import { Briefcase, Upload } from 'lucide-react';

const BecomeAProPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    customCategory: '',
    location: '',
    description: '',
    services: '',
    yearsInBusiness: '',
    priceRange: '$$',
    image: ''
  });

  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'professional') {
      toast({
        title: 'Professional Account Required',
        description: 'Please sign up as a professional to create a business profile.',
        variant: 'destructive'
      });
      navigate('/');
      return;
    }

    const fetchCategories = async () => {
      try {
        const data = await categoriesAPI.getAll();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [isAuthenticated, user, navigate, toast]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image smaller than 2MB',
        variant: 'destructive'
      });
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setFormData({ ...formData, image: base64 });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert comma-separated services to array
      const servicesArray = formData.services.split(',').map(s => s.trim());
      
      // Use custom category if "Other" was selected
      const finalCategory = formData.category === 'Other' ? formData.customCategory : formData.category;
      
      const providerData = {
        ...formData,
        category: finalCategory,
        services: servicesArray,
        yearsInBusiness: parseInt(formData.yearsInBusiness),
        email: user?.email,  // Link provider to user account
        subscriptionTier: user?.subscriptionTier || 'free'  // Set initial tier
      };

      await providersAPI.create(providerData);

      toast({
        title: 'Business Profile Created!',
        description: 'Your professional profile has been created successfully.',
      });

      // Reset form
      setFormData({
        name: '',
        category: '',
        customCategory: '',
        location: '',
        description: '',
        services: '',
        yearsInBusiness: '',
        priceRange: '$$',
        image: ''
      });

      // Redirect to homepage
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: 'Could not create business profile. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || user?.userType !== 'professional') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Create Your Business Profile</h1>
          <p className="text-gray-600 mt-2">Set up your professional profile to start receiving quote requests</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="h-5 w-5 mr-2 text-orange-600" />
              Business Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g., Maple Leaf Plumbing"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="category">Service Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value, customCategory: '' })} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select your service category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other (Specify Below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Category Input - Shows when "Other" is selected */}
              {formData.category === 'Other' && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                  <Label htmlFor="customCategory">Specify Your Service *</Label>
                  <Input
                    id="customCategory"
                    name="customCategory"
                    placeholder="e.g., Pool Maintenance, Window Cleaning, etc."
                    value={formData.customCategory}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-600 mt-1">Please specify your service category</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Service Area *</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="e.g., Toronto, ON"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="yearsInBusiness">Years in Business *</Label>
                  <Input
                    id="yearsInBusiness"
                    name="yearsInBusiness"
                    type="number"
                    min="0"
                    placeholder="e.g., 10"
                    value={formData.yearsInBusiness}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Business Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe your business, expertise, and what makes you stand out..."
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="mt-1 min-h-[100px]"
                />
              </div>

              <div>
                <Label htmlFor="services">Services Offered *</Label>
                <Textarea
                  id="services"
                  name="services"
                  placeholder="Enter services separated by commas (e.g., Emergency Repairs, Water Heater Installation, Drain Cleaning)"
                  value={formData.services}
                  onChange={handleChange}
                  required
                  className="mt-1 min-h-[80px]"
                />
                <p className="text-xs text-gray-500 mt-1">Separate each service with a comma</p>
              </div>

              <div>
                <Label htmlFor="priceRange">Price Range *</Label>
                <Select value={formData.priceRange} onValueChange={(value) => setFormData({ ...formData, priceRange: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$">$ - Budget Friendly</SelectItem>
                    <SelectItem value="$$">$$ - Moderate</SelectItem>
                    <SelectItem value="$$$">$$$ - Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Business Photo (Optional)</Label>
                <div className="mt-2">
                  {formData.image ? (
                    <div className="relative">
                      <img 
                        src={formData.image} 
                        alt="Business preview" 
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, image: '' })}
                        className="absolute top-2 right-2 bg-white"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => imageInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-orange-500 transition-colors"
                    >
                      <Upload className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload business photo</p>
                      <p className="text-xs text-gray-500 mt-1">Max 2MB • JPG, PNG, or WebP</p>
                    </div>
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>What happens next?</strong> Once you create your profile, you'll be visible in search results and start receiving quote requests from customers in your area.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Creating Profile...' : 'Create Business Profile'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default BecomeAProPage;
